/**
 * QR codes (ISO/IEC 18004), written for the till so it needs no library: the route QR on the
 * delivery receipt (prototype qrSvg) and later the bank-QR and the route of a courier. Byte mode
 * (UTF-8), error correction level M, versions 1 to 40, the mask with the lowest penalty. Based on the
 * well-known reference implementation by Project Nayuki (MIT).
 */

/** Error correction codewords per block, level M, per version (index 0 unused). */
const ECC_PER_BLOCK = [
  -1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26, 26, 28, 28, 28, 28, 28, 28, 28,
  28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
];
/** Error correction blocks, level M, per version (index 0 unused). */
const BLOCKS = [
  -1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18, 20, 21, 23, 25, 26, 28, 29, 31, 33,
  35, 37, 38, 40, 43, 45, 47, 49,
];
/** Format bits of level M. */
const LEVEL_M = 0;

/** A QR code as dark (true) and light modules, row by row. */
export interface QrCode {
  size: number;
  modules: boolean[][];
}

/** The QR code for a text; throws when the text does not fit in a QR code at all. */
export function encodeQr(text: string): QrCode {
  const data = [...new TextEncoder().encode(text)];
  let version = 1;
  for (; version <= 40; version++) {
    if (4 + countBits(version) + data.length * 8 <= dataCodewords(version) * 8) break;
  }
  if (version > 40) throw new RangeError("text too long for a QR code");

  // the bit stream: byte mode, the length, the data, terminator and padding
  const bits: number[] = [];
  const push = (value: number, length: number) => {
    for (let index = length - 1; index >= 0; index--) bits.push((value >>> index) & 1);
  };
  push(0b0100, 4);
  push(data.length, countBits(version));
  for (const byte of data) push(byte, 8);
  const capacity = dataCodewords(version) * 8;
  push(0, Math.min(4, capacity - bits.length));
  push(0, (8 - (bits.length % 8)) % 8);
  for (let pad = 0xec; bits.length < capacity; pad ^= 0xec ^ 0x11) push(pad, 8);
  const codewords: number[] = [];
  for (let index = 0; index < bits.length; index += 8) {
    codewords.push(bits.slice(index, index + 8).reduce((byte, bit) => (byte << 1) | bit, 0));
  }

  const qr = new Matrix(version);
  qr.drawFunctionPatterns();
  qr.drawCodewords(withErrorCorrection(version, codewords));
  // the mask with the lowest penalty, as the standard asks
  let best = 0;
  let lowest = Number.POSITIVE_INFINITY;
  for (let mask = 0; mask < 8; mask++) {
    qr.applyMask(mask);
    qr.drawFormatBits(mask);
    const penalty = qr.penalty();
    if (penalty < lowest) {
      best = mask;
      lowest = penalty;
    }
    qr.applyMask(mask); // undo (XOR)
  }
  qr.applyMask(best);
  qr.drawFormatBits(best);
  return { size: qr.size, modules: qr.modules };
}

/** An SVG path of the dark modules ("M0 0h1v1h-1z…"), for a viewBox of size × size. */
export function qrPath(code: QrCode): string {
  const parts: string[] = [];
  code.modules.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) parts.push(`M${String(x)} ${String(y)}h1v1h-1z`);
    });
  });
  return parts.join("");
}

function countBits(version: number): number {
  return version <= 9 ? 8 : 16;
}

/** Modules that carry data or error correction (everything except the function patterns). */
function rawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64;
  if (version >= 2) {
    const alignments = Math.floor(version / 7) + 2;
    result -= (25 * alignments - 10) * alignments - 55;
    if (version >= 7) result -= 36;
  }
  return result;
}

function dataCodewords(version: number): number {
  return Math.floor(rawDataModules(version) / 8) - ECC_PER_BLOCK[version]! * BLOCKS[version]!;
}

/** Splits the data in blocks, adds Reed-Solomon codewords to each and interleaves them. */
function withErrorCorrection(version: number, data: number[]): number[] {
  const blocks = BLOCKS[version]!;
  const eccLength = ECC_PER_BLOCK[version]!;
  const raw = Math.floor(rawDataModules(version) / 8);
  const shortBlocks = blocks - (raw % blocks);
  const shortLength = Math.floor(raw / blocks);
  const divisor = reedSolomonDivisor(eccLength);
  const all: number[][] = [];
  for (let index = 0, at = 0; index < blocks; index++) {
    const part = data.slice(at, at + shortLength - eccLength + (index < shortBlocks ? 0 : 1));
    at += part.length;
    const ecc = reedSolomonRemainder(part, divisor);
    if (index < shortBlocks) part.push(0);
    all.push([...part, ...ecc]);
  }
  const result: number[] = [];
  for (let index = 0; index < all[0]!.length; index++) {
    all.forEach((block, number) => {
      // the padding byte of a short block is not sent
      if (index !== shortLength - eccLength || number >= shortBlocks) result.push(block[index]!);
    });
  }
  return result;
}

function multiply(x: number, y: number): number {
  let z = 0;
  for (let index = 7; index >= 0; index--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d);
    z ^= ((y >>> index) & 1) * x;
  }
  return z;
}

function reedSolomonDivisor(degree: number): number[] {
  const result = Array.from({ length: degree }, () => 0);
  result[degree - 1] = 1;
  let root = 1;
  for (let index = 0; index < degree; index++) {
    for (let at = 0; at < degree; at++) {
      result[at] = multiply(result[at]!, root);
      if (at + 1 < degree) result[at]! ^= result[at + 1]!;
    }
    root = multiply(root, 0x02);
  }
  return result;
}

function reedSolomonRemainder(data: readonly number[], divisor: readonly number[]): number[] {
  const result = divisor.map(() => 0);
  for (const byte of data) {
    const factor = byte ^ result.shift()!;
    result.push(0);
    divisor.forEach((coefficient, index) => {
      result[index]! ^= multiply(coefficient, factor);
    });
  }
  return result;
}

const bit = (value: number, index: number) => ((value >>> index) & 1) !== 0;

class Matrix {
  readonly size: number;
  readonly modules: boolean[][];
  private readonly fixed: boolean[][];

  constructor(private readonly version: number) {
    this.size = version * 4 + 17;
    this.modules = Array.from({ length: this.size }, () => Array.from({ length: this.size }, () => false));
    this.fixed = Array.from({ length: this.size }, () => Array.from({ length: this.size }, () => false));
  }

  private set(x: number, y: number, dark: boolean): void {
    this.modules[y]![x] = dark;
    this.fixed[y]![x] = true;
  }

  drawFunctionPatterns(): void {
    for (let index = 0; index < this.size; index++) {
      this.set(6, index, index % 2 === 0);
      this.set(index, 6, index % 2 === 0);
    }
    this.finder(3, 3);
    this.finder(this.size - 4, 3);
    this.finder(3, this.size - 4);
    const positions = this.alignmentPositions();
    const last = positions.length - 1;
    positions.forEach((x, i) => {
      positions.forEach((y, j) => {
        // not on top of the finder patterns
        if (!((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0))) this.alignment(x, y);
      });
    });
    this.drawFormatBits(0);
    this.drawVersion();
  }

  private finder(x: number, y: number): void {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const distance = Math.max(Math.abs(dx), Math.abs(dy));
        const xx = x + dx;
        const yy = y + dy;
        if (xx >= 0 && xx < this.size && yy >= 0 && yy < this.size) this.set(xx, yy, distance !== 2 && distance !== 4);
      }
    }
  }

  private alignment(x: number, y: number): void {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) this.set(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  }

  private alignmentPositions(): number[] {
    if (this.version === 1) return [];
    const count = Math.floor(this.version / 7) + 2;
    const step = this.version === 32 ? 26 : Math.ceil((this.version * 4 + 4) / (count * 2 - 2)) * 2;
    const result = [6];
    for (let position = this.size - 7; result.length < count; position -= step) result.splice(1, 0, position);
    return result;
  }

  drawFormatBits(mask: number): void {
    const data = (LEVEL_M << 3) | mask;
    let remainder = data;
    for (let index = 0; index < 10; index++) remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537);
    const bits = ((data << 10) | remainder) ^ 0x5412;
    for (let index = 0; index <= 5; index++) this.set(8, index, bit(bits, index));
    this.set(8, 7, bit(bits, 6));
    this.set(8, 8, bit(bits, 7));
    this.set(7, 8, bit(bits, 8));
    for (let index = 9; index < 15; index++) this.set(14 - index, 8, bit(bits, index));
    for (let index = 0; index < 8; index++) this.set(this.size - 1 - index, 8, bit(bits, index));
    for (let index = 8; index < 15; index++) this.set(8, this.size - 15 + index, bit(bits, index));
    this.set(8, this.size - 8, true); // always dark
  }

  private drawVersion(): void {
    if (this.version < 7) return;
    let remainder = this.version;
    for (let index = 0; index < 12; index++) remainder = (remainder << 1) ^ ((remainder >>> 11) * 0x1f25);
    const bits = (this.version << 12) | remainder;
    for (let index = 0; index < 18; index++) {
      const dark = bit(bits, index);
      const a = this.size - 11 + (index % 3);
      const b = Math.floor(index / 3);
      this.set(a, b, dark);
      this.set(b, a, dark);
    }
  }

  /** The data in the zigzag order of the standard, two columns at a time from the bottom right. */
  drawCodewords(data: readonly number[]): void {
    let index = 0;
    for (let right = this.size - 1; right >= 1; right -= 2) {
      if (right === 6) right = 5; // the vertical timing pattern
      for (let vertical = 0; vertical < this.size; vertical++) {
        for (let column = 0; column < 2; column++) {
          const x = right - column;
          const upward = ((right + 1) & 2) === 0;
          const y = upward ? this.size - 1 - vertical : vertical;
          if (!this.fixed[y]![x] && index < data.length * 8) {
            this.modules[y]![x] = bit(data[index >>> 3]!, 7 - (index & 7));
            index++;
          }
        }
      }
    }
  }

  /** XORs the data modules with a mask pattern (applying it twice undoes it). */
  applyMask(mask: number): void {
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        if (this.fixed[y]![x]) continue;
        let invert: boolean;
        switch (mask) {
          case 0:
            invert = (x + y) % 2 === 0;
            break;
          case 1:
            invert = y % 2 === 0;
            break;
          case 2:
            invert = x % 3 === 0;
            break;
          case 3:
            invert = (x + y) % 3 === 0;
            break;
          case 4:
            invert = (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
            break;
          case 5:
            invert = ((x * y) % 2) + ((x * y) % 3) === 0;
            break;
          case 6:
            invert = (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
            break;
          default:
            invert = (((x + y) % 2) + ((x * y) % 3)) % 2 === 0;
        }
        if (invert) this.modules[y]![x] = !this.modules[y]![x];
      }
    }
  }

  /** The penalty of the standard: runs, 2×2 blocks, finder-like patterns and the dark/light balance. */
  penalty(): number {
    let result = 0;
    const lines = (vertical: boolean) => {
      for (let a = 0; a < this.size; a++) {
        let color = false;
        let run = 0;
        const history = [0, 0, 0, 0, 0, 0, 0];
        for (let b = 0; b < this.size; b++) {
          const dark = vertical ? this.modules[b]![a]! : this.modules[a]![b]!;
          if (dark === color) {
            run++;
            if (run === 5) result += 3;
            else if (run > 5) result++;
          } else {
            this.addHistory(run, history);
            if (!color) result += this.finderLike(history) * 40;
            color = dark;
            run = 1;
          }
        }
        result += this.terminate(color, run, history) * 40;
      }
    };
    lines(false);
    lines(true);
    let dark = 0;
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        const color = this.modules[y]![x]!;
        if (color) dark++;
        if (
          x < this.size - 1 &&
          y < this.size - 1 &&
          color === this.modules[y]![x + 1] &&
          color === this.modules[y + 1]![x] &&
          color === this.modules[y + 1]![x + 1]
        ) {
          result += 3;
        }
      }
    }
    const total = this.size * this.size;
    result += (Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1) * 10;
    return result;
  }

  private addHistory(run: number, history: number[]): void {
    const length = history[0] === 0 ? run + this.size : run; // the light border before the first run
    history.pop();
    history.unshift(length);
  }

  private finderLike(history: readonly number[]): number {
    const n = history[1]!;
    const core = n > 0 && history[2] === n && history[3] === n * 3 && history[4] === n && history[5] === n;
    return (
      (core && history[0]! >= n * 4 && history[6]! >= n ? 1 : 0) +
      (core && history[6]! >= n * 4 && history[0]! >= n ? 1 : 0)
    );
  }

  private terminate(color: boolean, run: number, history: number[]): number {
    let length = run;
    if (color) {
      this.addHistory(length, history);
      length = 0;
    }
    length += this.size; // the light border after the last run
    this.addHistory(length, history);
    return this.finderLike(history);
  }
}
